/**
 * StrictnessControl — "Set how strict this check should be."
 *
 * The coach-facing half of the tolerance review (observability Phase 4). Used
 * by every angle-check editor in the template studio (axis line, joint angle),
 * so a coach meets the same question in the same words wherever they draw one.
 *
 * It shows three things the studio never showed before:
 *   1. the current number of degrees,
 *   2. whether that number is the standard setting or one the coach chose,
 *   3. the prompt to choose — what / why / how, per
 *      docs/COACH_FACING_COPY_STANDARD.md §3.
 *
 * Typing a number reports `tolerance_source: 'coach'`; "Use the standard
 * setting" restores `standardDegrees` and reports `'default'`. Both go into the
 * saved goal config, which is how the backend's finalize summary stops counting
 * the check as unreviewed.
 *
 * NO strict / normal / relaxed buttons. Each band would be a new number with
 * no experiment behind it — the class-D constant ledger row HC-26 exists to
 * retire exactly those, and the preset trio the joint-angle editor used to show
 * (25 / 15 / 8, ledger HC-30) is the direct cause of the 29-of-41 figure this
 * surface was built to fix. The coach types the degrees they would accept.
 *
 * Copy lives in `@/utils/toleranceReviewCopy`; this component is layout only.
 */
import { useId } from 'react';
import { cn } from '@/utils/cn';
import {
  COACH_SET_BADGE,
  STANDARD_SETTING_BADGE,
  STRICTNESS_FIELD_LABEL,
  USE_STANDARD_SETTING_ACTION,
  coachSetStrictnessNote,
  standardStrictnessPrompt,
  strictnessValueText,
} from '@/utils/toleranceReviewCopy';
import type { StrictnessReference } from '@/utils/toleranceReviewCopy';

export interface StrictnessControlProps {
  /** The checkpoint name the coach typed — quoted back to them in the prompt. */
  label: string;
  /** Current degrees. Undefined/null falls back to `standardDegrees`. */
  degrees?: number | null;
  /**
   * Stored provenance. `undefined`/`null` means a check saved before this
   * control existed; it is read as the standard setting, which is also how the
   * backend's summary reads it when the value equals the default.
   */
  source?: 'default' | 'coach' | null;
  /**
   * The standard setting for this kind of check — passed in by the caller so
   * this component introduces no number of its own.
   */
  standardDegrees: number;
  /**
   * What the degrees are measured against: the coach's own demo, or an angle
   * they typed in. Decided by the editor from the check's scoring method, so
   * the sentence the coach reads is true of their check.
   */
  comparedTo?: StrictnessReference;
  /** Called with the new degrees and the provenance to store alongside it. */
  onChange: (degrees: number, source: 'default' | 'coach') => void;
  /** Narrowest / widest the field accepts, matching the backend field bounds. */
  min?: number;
  max?: number;
  className?: string;
}

/**
 * Backend bounds on `angle_tolerance_degrees` (`gt=0.0, le=45.0` on both
 * AxisConfig and JointAngleConfig). Mirrored rather than invented so the field
 * cannot offer a number the save would reject.
 */
const FIELD_MIN_DEGREES = 1;
const FIELD_MAX_DEGREES = 45;

export default function StrictnessControl({
  label,
  degrees,
  source,
  standardDegrees,
  comparedTo = 'demo',
  onChange,
  min = FIELD_MIN_DEGREES,
  max = FIELD_MAX_DEGREES,
  className,
}: StrictnessControlProps) {
  const fieldId = useId();
  const currentDegrees = degrees ?? standardDegrees;

  // A check is "on the standard setting" when the coach has not declared
  // otherwise. A legacy check (no `source`) counts as standard only when its
  // value still IS the standard one — a coach who typed 8° years ago must not
  // be told the system chose it. Same rule as the backend's
  // `resolve_tolerance_source`, which is what the review panel counts with.
  const isStandard =
    source === 'coach' ? false : source === 'default' ? true : currentDegrees === standardDegrees;

  const prompt = standardStrictnessPrompt(label);
  const note = coachSetStrictnessNote(currentDegrees, comparedTo);

  const handleDegreesChange = (raw: string) => {
    const parsed = Number.parseFloat(raw);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.min(Math.max(parsed, min), max);
    onChange(clamped, 'coach');
  };

  return (
    <div
      className={cn('space-y-2', className)}
      data-testid="strictness-control"
      data-strictness-source={isStandard ? 'default' : 'coach'}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-xs font-semibold text-text-primary">
          {isStandard ? prompt.what : note.what}
        </span>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[10px] font-medium',
            isStandard
              ? 'bg-amber-500/15 text-amber-300'
              : 'bg-success/15 text-success',
          )}
          data-testid="strictness-badge"
        >
          {isStandard ? STANDARD_SETTING_BADGE : COACH_SET_BADGE}
        </span>
      </div>

      {isStandard && (
        <p
          className="text-[11px] leading-relaxed text-text-secondary"
          data-testid="strictness-current-value"
        >
          Currently {strictnessValueText(currentDegrees, comparedTo)}.
        </p>
      )}

      <p className="text-[11px] leading-relaxed text-text-secondary">
        {isStandard ? prompt.why : note.why}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <label
          htmlFor={fieldId}
          className="text-[11px] font-medium text-text-secondary"
        >
          {STRICTNESS_FIELD_LABEL}
        </label>
        <input
          id={fieldId}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={1}
          value={currentDegrees}
          onChange={(e) => handleDegreesChange(e.target.value)}
          className={cn(
            'w-20 rounded-lg border border-border-subtle bg-dark-elevated-1 px-2.5 py-1.5',
            'text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-amber-500/40',
          )}
          data-testid="strictness-degrees-input"
        />
        <span className="text-[11px] text-text-secondary">degrees</span>
        {!isStandard && (
          <button
            type="button"
            onClick={() => onChange(standardDegrees, 'default')}
            className={cn(
              'rounded-lg border border-border-subtle px-2.5 py-1.5 text-[11px] font-medium',
              'text-text-secondary transition-colors hover:bg-dark-elevated-2',
            )}
            data-testid="strictness-use-standard"
          >
            {USE_STANDARD_SETTING_ACTION}
          </button>
        )}
      </div>

      <p className="text-[11px] leading-relaxed text-text-muted">
        {isStandard ? prompt.how : note.how}
      </p>
    </div>
  );
}
